import {
  FlexBox,
  InputSelectionListChangeEvent,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
  Text,
} from '@dixa/lib-shared-habla';
import { useState } from 'react';

import { smsApi, SmsPhoneNumberAndQueue } from 'apis/sms';
import { InputQueuesSelect } from 'components/inputQueuesSelect';
import { NotificationType, useNotifications } from 'hooks/useNotifications';
import { createAppModal } from 'utils/modal';

import styles from './editSmsQueueModal.module.scss';

/** A component that displays a modal for changing the sms queue. */
export const EditSmsQueueModal = createAppModal(
  { type: 'settings:editSmsQueue' },
  ({ isOpen, modal, hide }): JSX.Element => {
    const notify = useNotifications();
    const [selectedQueues, setSelectedQueues] = useState<
      SmsPhoneNumberAndQueue['queue'][]
    >(
      modal.arg.smsTableDataItem.queue
        ? [modal.arg.smsTableDataItem.queue]
        : [],
    );
    const selectedQueue: SmsPhoneNumberAndQueue['queue'] | undefined =
      selectedQueues[0];
    const [updateSmsNumberQueue, { isLoading }] =
      smsApi.useUpdateSmsNumberQueueMutation();

    /** Handles closing the modal. */
    const handleClose = () => {
      setSelectedQueues([]);
      hide?.();
    };

    /** Handles selecting a queue. */
    const handleQueueSelect = ({
      selectedValues,
    }: InputSelectionListChangeEvent<SmsPhoneNumberAndQueue['queue']>) => {
      setSelectedQueues(selectedValues);
    };

    /** Handles confirming a queue. */
    const handleConfirm = async () => {
      if (
        selectedQueue &&
        selectedQueue.id !== modal.arg.smsTableDataItem.queue?.id
      ) {
        try {
          await updateSmsNumberQueue({
            phoneNumber: modal.arg.smsTableDataItem.phoneNumber.e164_format,
            queueId: selectedQueue.id,
          }).unwrap();

          notify(
            {
              title: 'Queue successfully updated',
              time: 3000,
            },
            NotificationType.Success,
          );
          handleClose();
        } catch {
          notify(
            {
              title: 'An error occured while updating the queue',
            },
            NotificationType.Error,
          );
        }
      }
    };

    return (
      <Modal isOpen={isOpen} onClose={handleClose} className={styles.modal}>
        <ModalHead
          title={
            <FlexBox alignItems="baseline" gap="200">
              <Text.Heading>Edit text message queue</Text.Heading>

              <Text.Paragraph subdued>
                {modal.arg.smsTableDataItem.phoneNumber.e164_format}
              </Text.Paragraph>
            </FlexBox>
          }
          onClose={handleClose}
        />

        <ModalBody>
          <FlexBox flexDirection="column" gap="400">
            <Text.Paragraph subdued>
              Please select which queue all incoming text messages will go to.
            </Text.Paragraph>

            <FlexBox flexDirection="column" gap="200">
              <Text.Paragraph variant="3">
                Queue for incoming text messages
              </Text.Paragraph>

              <InputQueuesSelect<SmsPhoneNumberAndQueue['queue']>
                value={selectedQueues}
                onChange={handleQueueSelect}
              />
            </FlexBox>
          </FlexBox>
        </ModalBody>

        <ModalFoot
          cancelOnClick={handleClose}
          confirmName="Update"
          confirmOnClick={handleConfirm}
          confirmProps={{
            disabled:
              !selectedQueue ||
              selectedQueue.id === modal.arg.smsTableDataItem.queue?.id,
            isLoading,
          }}
        />
      </Modal>
    );
  },
);
